/**
 * Document totals — invoices and estimates share this exactly.
 *
 * This is the single source of truth for "what does this document add up to".
 * The API calls it before persisting, the web app calls it to render a live
 * preview while you type, and the printable view calls it again. All three get
 * identical numbers because there is only one implementation.
 *
 * Ordering matters and is fixed:
 *   1. line totals (the only rounding of a fractional quantity)
 *   2. subtotal
 *   3. discount, applied to the whole document
 *   4. discount split across taxable / non-taxable buckets by largest remainder
 *   5. tax on the DISCOUNTED taxable base
 *   6. total = subtotal - discount + tax
 *   7. balance = total - payments
 */
import { addCents, allocateCents, applyBps, clampCents, lineTotal, } from './money.js';
export function computeTotals(input) {
    const taxRateBps = input.taxRateBps ?? 0;
    const discountType = input.discountType ?? 'none';
    const lineTotalsCents = input.lineItems.map((li) => lineTotal(li.unitPriceCents, li.quantity));
    const subtotalCents = addCents(...lineTotalsCents, 0);
    const taxableSubtotalCents = addCents(...input.lineItems.map((li, i) => (li.taxable ? lineTotalsCents[i] : 0)), 0);
    const nonTaxableSubtotalCents = subtotalCents - taxableSubtotalCents;
    let discountCents = 0;
    if (discountType === 'percent') {
        const bps = clampBps(input.discountValueBps ?? 0);
        discountCents = applyBps(subtotalCents, bps);
    }
    else if (discountType === 'fixed') {
        discountCents = input.discountValueCents ?? 0;
    }
    // A discount can never exceed the subtotal or go negative.
    discountCents = clampCents(discountCents, 0, Math.max(subtotalCents, 0));
    // Split the discount across the two buckets so no cent is invented or lost.
    const [taxableDiscountCents = 0] = allocateCents(discountCents, [
        Math.max(taxableSubtotalCents, 0),
        Math.max(nonTaxableSubtotalCents, 0),
    ]);
    const taxableBaseCents = taxableSubtotalCents - taxableDiscountCents;
    const taxCents = applyBps(Math.max(taxableBaseCents, 0), clampBps(taxRateBps));
    const totalCents = subtotalCents - discountCents + taxCents;
    const amountPaidCents = addCents(...(input.payments ?? []).filter((p) => !p.voidedAt).map((p) => p.amountCents), 0);
    const balanceDueCents = totalCents - amountPaidCents;
    return {
        lineTotalsCents,
        subtotalCents,
        taxableSubtotalCents,
        discountCents,
        taxableDiscountCents,
        taxableBaseCents,
        taxCents,
        totalCents,
        amountPaidCents,
        balanceDueCents,
        isSettled: balanceDueCents <= 0,
        overpaidCents: balanceDueCents < 0 ? -balanceDueCents : 0,
    };
}
function clampBps(bps) {
    if (!Number.isInteger(bps))
        return Math.round(bps);
    // 100_000 bps = 1000%, a generous ceiling that still catches unit mix-ups.
    return Math.min(Math.max(bps, 0), 100_000);
}
export function paymentState(totals) {
    if (totals.totalCents <= 0 && totals.amountPaidCents === 0)
        return 'paid';
    if (totals.balanceDueCents < 0)
        return 'overpaid';
    if (totals.balanceDueCents === 0)
        return 'paid';
    if (totals.amountPaidCents > 0)
        return 'partial';
    return 'unpaid';
}
export const INVOICE_PAYMENT_STATE_LABELS = {
    unpaid: 'Unpaid',
    partial: 'Partially paid',
    paid: 'Paid',
    overpaid: 'Overpaid',
};
/**
 * How much of a proposed payment can actually be applied. Prevents the UI from
 * offering to take more than is owed without an explicit override.
 */
export function maxApplicablePayment(totals) {
    return Math.max(totals.balanceDueCents, 0);
}
/** Days past due, or 0 if not yet due. `now` injectable for testability. */
export function daysOverdue(dueDate, now = new Date()) {
    if (!dueDate)
        return 0;
    const due = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
    const ms = now.getTime() - due.getTime();
    if (ms <= 0)
        return 0;
    return Math.floor(ms / 86_400_000);
}
export function agingBucket(days) {
    if (days <= 0)
        return 'current';
    if (days <= 30)
        return '1_30';
    if (days <= 60)
        return '31_60';
    if (days <= 90)
        return '61_90';
    return '90_plus';
}
export const AGING_BUCKET_LABELS = {
    current: 'Current',
    '1_30': '1–30 days',
    '31_60': '31–60 days',
    '61_90': '61–90 days',
    '90_plus': '90+ days',
};
//# sourceMappingURL=totals.js.map